import React, { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import styled from 'styled-components';
import { Container, Card, Button, Input, Label, ErrorMessage } from '../styles/GlobalStyles';

const ResetPasswordContainer = styled(Container)`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: var(--spacing-md);
`;

const ResetPasswordCard = styled(Card)`
  width: 100%;
  max-width: 400px;
  margin: 0;
`;

const Title = styled.h1`
  text-align: center;
  margin-bottom: var(--spacing-md);
  color: var(--text-primary);
  font-size: 1.875rem;
  font-weight: 700;
`;

const Description = styled.p`
  text-align: center;
  color: var(--text-secondary);
  margin-bottom: var(--spacing-xl);
  font-size: 0.875rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
`;

const LinkContainer = styled.div`
  text-align: center;
  margin-top: var(--spacing-lg);
`;

const StyledLink = styled(Link)`
  color: var(--primary-color);
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const SuccessMessage = styled.div`
  background-color: #d1fae5;
  border: 1px solid #10b981;
  color: #065f46;
  padding: var(--spacing-md);
  border-radius: 4px;
  margin-bottom: var(--spacing-lg);
  text-align: center;
`;

const schema = yup.object({
  newPassword: yup
    .string()
    .min(8, 'Password must be at least 8 characters')
    .required('Password is required'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('newPassword')], 'Passwords must match')
    .required('Please confirm your password'),
});

type ResetPasswordFormData = yup.InferType<typeof schema>;

export const ResetPasswordPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState<string>('');
  const [isSuccess, setIsSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordFormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!token) {
      setServerError('Invalid reset link');
      return;
    }

    setIsLoading(true);
    setServerError('');
    
    try {
      const { authService } = await import('../services/authService');
      await authService.resetPassword(token, data.newPassword);
      setIsSuccess(true);
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error: any) {
      setServerError(error.response?.data?.error?.message || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ResetPasswordContainer>
      <ResetPasswordCard>
        <Title>Reset Password</Title>
        <Description>
          Enter your new password below.
        </Description>
        
        {isSuccess && (
          <SuccessMessage>
            Password reset successfully! Redirecting to login...
          </SuccessMessage>
        )}
        
        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormGroup>
            <Label htmlFor="newPassword">New Password</Label>
            <Input
              id="newPassword"
              type="password"
              {...register('newPassword')}
              className={errors.newPassword ? 'error' : ''}
              disabled={isSuccess}
            />
            {errors.newPassword && <ErrorMessage>{errors.newPassword.message}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label htmlFor="confirmPassword">Confirm Password</Label>
            <Input
              id="confirmPassword"
              type="password"
              {...register('confirmPassword')}
              className={errors.confirmPassword ? 'error' : ''}
              disabled={isSuccess}
            />
            {errors.confirmPassword && <ErrorMessage>{errors.confirmPassword.message}</ErrorMessage>}
          </FormGroup>

          {serverError && <ErrorMessage>{serverError}</ErrorMessage>}

          <Button type="submit" disabled={isLoading || isSuccess}>
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </Button>
        </Form>

        <LinkContainer>
          <StyledLink to="/login">Back to Sign In</StyledLink>
        </LinkContainer>
      </ResetPasswordCard>
    </ResetPasswordContainer>
  );
};
